import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';

import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { FirebaseService } from 'app/services/firebase.service';

type MetricRow = {
  label: string;
  count: number;
};

type WatcherRow = {
  id: string;
  userId: string;
  displayName: string;
  status: string;
  createdAtLabel: string;
  checkedAtLabel: string;
  expiresOnLabel: string;
  seatsLabel: string;
  route: string;
};

type UserRecord = {
  id: string;
  email?: string;
  displayName?: string;
  mobileNumber?: string;
  isMobileVerified?: boolean;
  accountType?: string;
  memberships?: any[];
  createdAt?: any;
  [key: string]: any;
};

@Component({
  selector: 'app-admin-hq-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, SpinnerComponent],
  templateUrl: './admin-hq-dashboard.component.html',
  styleUrls: ['./admin-hq-dashboard.component.scss'],
})
export class AdminHqDashboardComponent implements OnInit {
  isLoading = true;
  error = '';
  loadedAtLabel = '';

  totalUsers = 0;
  newUsers24h = 0;
  newUsers7d = 0;
  usersWithMembership = 0;
  usersWithoutAcceptedMembership = 0;
  pendingMembershipReviewCount = 0;

  membershipTypeRows: MetricRow[] = [];
  onboardingStageRows: MetricRow[] = [];
  latestWatchers: WatcherRow[] = [];
  latestPersistentWatchers: WatcherRow[] = [];

  constructor(private firebaseService: FirebaseService) {}

  ngOnInit(): void {
    void this.loadDashboard();
  }

  async loadDashboard(): Promise<void> {
    this.isLoading = true;
    this.error = '';

    try {
      const [
        userSnap,
        pendingReviewSnap,
        watcherSnap,
        persistentWatcherSnap,
      ] = await Promise.all([
        getDocs(collection(this.firebaseService.firestore, 'users')),
        getDocs(collection(this.firebaseService.firestore, 'pending-membership-verifications')),
        getDocs(query(collection(this.firebaseService.firestore, 'flight-watchers'), orderBy('createdAt', 'desc'), limit(100))),
        getDocs(query(collection(this.firebaseService.firestore, 'persistent-flight-watchers'), orderBy('createdAt', 'desc'), limit(100))),
      ]);

      const users = userSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as UserRecord));
      const pendingReviews = pendingReviewSnap.docs.map(docSnap => docSnap.data());
      const pendingReviewUserIds = new Set(
        pendingReviews
          .map(review => typeof review?.userId === 'string' ? review.userId : '')
          .filter(Boolean)
      );

      this.totalUsers = users.length;
      this.pendingMembershipReviewCount = pendingReviewSnap.size;
      this.newUsers24h = this.countNewUsers(users, 1);
      this.newUsers7d = this.countNewUsers(users, 7);
      this.usersWithMembership = users.filter(user => this.getMemberships(user).length > 0).length;
      this.usersWithoutAcceptedMembership = users.filter(user => !this.hasAcceptedMembership(user)).length;
      this.membershipTypeRows = this.buildMembershipTypeRows(users);
      this.onboardingStageRows = this.buildOnboardingStageRows(users, pendingReviewUserIds);
      this.latestWatchers = watcherSnap.docs.map(docSnap => this.toWatcherRow(docSnap.id, docSnap.data(), 'watcher'));
      this.latestPersistentWatchers = persistentWatcherSnap.docs.map(docSnap => this.toWatcherRow(docSnap.id, docSnap.data(), 'persistent-watcher'));
      this.loadedAtLabel = this.formatDate(new Date());
    } catch (err: any) {
      console.error('Failed to load admin HQ dashboard', err);
      this.error = err?.message || 'Unable to load dashboard data.';
    } finally {
      this.isLoading = false;
    }
  }

  private countNewUsers(users: UserRecord[], days: number): number {
    const since = Date.now() - days * 24 * 60 * 60 * 1000;
    return users.filter(user => {
      const createdAt = this.toDate(user.createdAt);
      return createdAt ? createdAt.getTime() >= since : false;
    }).length;
  }

  private buildMembershipTypeRows(users: UserRecord[]): MetricRow[] {
    const counts = new Map<string, number>();

    users.forEach(user => {
      const memberships = this.getMemberships(user);
      const seenTypes = new Set<string>();

      memberships.forEach(membership => {
        const type = this.membershipTypeLabel(membership);
        seenTypes.add(type);
      });

      seenTypes.forEach(type => counts.set(type, (counts.get(type) || 0) + 1));
    });

    return this.mapToSortedRows(counts);
  }

  private buildOnboardingStageRows(users: UserRecord[], pendingReviewUserIds: Set<string>): MetricRow[] {
    const counts = new Map<string, number>();

    users
      .filter(user => !this.hasAcceptedMembership(user))
      .forEach(user => {
        const stage = this.getOnboardingStage(user, pendingReviewUserIds);
        counts.set(stage, (counts.get(stage) || 0) + 1);
      });

    return this.mapToSortedRows(counts);
  }

  private getOnboardingStage(user: UserRecord, pendingReviewUserIds: Set<string>): string {
    const memberships = this.getMemberships(user);

    if (!user.mobileNumber || user.isMobileVerified !== true) {
      return 'Phone verification';
    }

    if (!user.accountType) {
      return 'Account type';
    }

    if (memberships.length === 0) {
      return 'Membership setup';
    }

    if (memberships.some(membership => membership?.statementRequiresHardReview === true || membership?.statementStatus === 'hard_required')) {
      return 'Hard verification required';
    }

    if (pendingReviewUserIds.has(user.id)) {
      return 'Admin membership review';
    }

    if (memberships.some(membership => membership?.statementStatus === 'review')) {
      return 'Document review';
    }

    if (memberships.some(membership => membership?.statementStatus === 'failed')) {
      return 'Document failed';
    }

    return 'Membership document upload';
  }

  private membershipTypeLabel(membership: any): string {
    const type = typeof membership?.type === 'string' ? membership.type.trim() : '';
    if (type) {
      return type;
    }

    const provider = typeof membership?.provider === 'string' ? membership.provider.trim() : '';
    if (provider) {
      return provider === 'independent' ? 'Independent' : provider;
    }

    return 'Unspecified';
  }

  private hasAcceptedMembership(user: UserRecord): boolean {
    return this.getMemberships(user).some(membership =>
      membership?.statementVerified === true ||
      membership?.statementAccessGranted === true ||
      membership?.provider === 'independent'
    );
  }

  private getMemberships(user: UserRecord): any[] {
    return Array.isArray(user.memberships) ? user.memberships : [];
  }

  private mapToSortedRows(counts: Map<string, number>): MetricRow[] {
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }

  private toWatcherRow(id: string, data: any, routePrefix: 'watcher' | 'persistent-watcher'): WatcherRow {
    return {
      id,
      userId: data?.userId || '- -',
      displayName: data?.displayName || this.fallbackWatcherDisplayName(data),
      status: data?.status || 'active',
      createdAtLabel: this.formatDate(data?.createdAt),
      checkedAtLabel: this.formatDate(data?.checkedAt),
      expiresOnLabel: this.formatDate(data?.expiresOn),
      seatsLabel: this.formatSeats(data),
      route: `/${routePrefix}/${id}`,
    };
  }

  private fallbackWatcherDisplayName(data: any): string {
    const from = data?.departureLocationName || 'Unknown departure';
    const to = data?.arrivalLocationName || data?.arrivalGeneralType || 'Unknown arrival';
    return `${from} to ${to}`;
  }

  private formatSeats(data: any): string {
    const seatsNeeded = data?.seatsNeeded;
    const seatsOffered = data?.seatsOffered;

    if (seatsNeeded) {
      return `${seatsNeeded} needed`;
    }

    if (seatsOffered) {
      return `${seatsOffered} offered`;
    }

    return '- -';
  }

  private formatDate(value: any): string {
    const date = this.toDate(value);
    if (!date) {
      return '- -';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }

  private toDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }
}
